import { getFileExtensionByUrl, getFileSizeByUrl } from './url';

export type TrackFn = (event: string, params: Record<string, unknown>) => void;

export type DownloadTrackParams = {
    /** 下载文件的地址 */
    resultURL?: string;
    /** 下载文件的 Blob 对象（与 resultUrl 二选一，优先使用 blob 获取 fileSize/fileType） */
    blob?: Blob | File;
    /** 文件后缀类型，未传时自动从 resultUrl 或 blob.type 解析 */
    fileType?: string;
    /** 文件大小（字节数），未传时自动根据 blob.size 或 resultUrl 请求获取；上报时会转换为 MB 数值 */
    fileSize?: string | number;
    /** 下载结果：1 成功，0 失败 */
    success: 1 | 0;
    /** 下载尝试次数 */
    tryTimes?: number;
};

type DownloadTrackPayload = {
    result_url: string;
    file_type: string;
    file_size: string;
    duration: number;
    success: 1 | 0;
    try_times: number;
};

/**
 * 从 MIME type 中提取文件后缀
 * @example getMimeExtension('application/zip') => 'zip'
 * @example getMimeExtension('image/png') => 'png'
 */
function getMimeExtension(mimeType: string): string {
    if (!mimeType) return '';
    const subType = mimeType.split('/')[1] || '';
    // 处理复合子类型，如 svg+xml => svg
    return subType.split('+')[0].toLowerCase();
}

/** 统一 fileType 为后缀名：`image/png` => `png`，`png` 保持不变 */
function normalizeFileType(fileType: string): string {
    if (!fileType) return '';
    if (fileType.includes('/')) {
        return getMimeExtension(fileType);
    }
    return fileType.toLowerCase();
}

/** 将字节数转换为以 MB 为单位的数值字符串，如 `1.25` */
function formatFileSizeWithUnit(size?: number | string): string {
    const bytes = typeof size === 'string' ? Number(size) : size;

    if (!bytes || Number.isNaN(bytes) || bytes <= 0) {
        return '0';
    }

    return (bytes / 1024 / 1024).toFixed(2);
}

/** 埋点 result_url：base64 / blob 场景固定上报占位值，避免上报真实内容 */
function getTrackResultUrl(resultURL?: string, blob?: Blob): string {
    try {
        if (blob || resultURL?.startsWith('blob:')) {
            return 'blob:url';
        }
        if (resultURL?.startsWith('data:') || resultURL?.startsWith('base64')) {
            return 'base64:url';
        }
        if (!resultURL || !resultURL.includes('http')) {
            return 'blob:url';
        }
        return resultURL || 'blob:url';
    } catch (error) {
        return 'blob:url';
    }
}

/** 组装 download_file_to_local 埋点参数 */
export async function buildDownloadTrackPayload(
    params: DownloadTrackParams,
    startedAt: number,
): Promise<DownloadTrackPayload> {
    const { blob, resultURL, success, tryTimes } = params;

    const fileType = normalizeFileType(
        params.fileType
            ? params.fileType
            : blob
              ? getMimeExtension(blob.type)
              : resultURL
                ? getFileExtensionByUrl(resultURL)
                : '',
    );

    const fileSizeInBytes = params.fileSize
        ? Number(params.fileSize)
        : blob
          ? blob.size
          : resultURL
            ? await getFileSizeByUrl(resultURL)
            : undefined;

    return {
        result_url: getTrackResultUrl(resultURL, blob),
        file_type: fileType || '',
        file_size: formatFileSizeWithUnit(fileSizeInBytes),
        duration: Number(((Date.now() - startedAt) / 1000).toFixed(2)),
        success,
        try_times: tryTimes || 0,
    };
}

/**
 * 下载埋点会话：在实例创建时开始计时，report 时上报 download_file_to_local。
 * 需在商业化校验等前置逻辑完成后再 new，或通过 markDownloadStart() 重置计时起点。
 */
export class DownloadTrackSession {
    private startedAt: number;
    private track: TrackFn;

    constructor(track: TrackFn, startedAt = Date.now()) {
        this.track = track;
        this.startedAt = startedAt;
    }

    /** 重置计时起点（商业化校验通过后、真正开始下载前调用） */
    markDownloadStart() {
        this.startedAt = Date.now();
        return this;
    }

    async report(params: DownloadTrackParams) {
        const payload = await buildDownloadTrackPayload(params, this.startedAt);
        this.track('download_file_to_local', payload);
    }
}
